import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import VisualPageBuilder, { PageData } from "@/components/admin/VisualPageBuilder";
import { ArrowLeft, Eye, Save, Share } from "lucide-react";

interface Template {
  id: string;
  name: string;
  description: string | null;
  category: string;
  is_active: boolean;
  preview_image_url: string | null;
  template_data: any;
}

export default function TemplateBuilder() {
  const { templateId } = useParams<{ templateId: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [template, setTemplate] = useState<Template | null>(null);
  const [tplData, setTplData] = useState<PageData | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [meta, setMeta] = useState({
    name: "",
    description: "",
    category: "general",
    preview_image_url: ""
  });

  useEffect(() => {
    if (templateId) loadTemplate();
  }, [templateId]);

  const loadTemplate = async () => {
    try {
      const { data, error } = await supabase
        .from("landing_page_templates")
        .select("*")
        .eq("id", templateId)
        .single();
      if (error) throw error;
      setTemplate(data as any);
      const d = (data as any).template_data || {};
      setTplData({
        elements: Array.isArray(d.elements) ? d.elements : [],
        globalStyles: d.globalStyles || {
          colors: { primary: "#3b82f6", secondary: "#6b7280", accent: "#10b981", background: "#ffffff", text: "#111827" },
          fonts: { primary: "Inter", secondary: "Inter" },
          spacing: { small: "8px", medium: "16px", large: "32px" }
        },
        responsive: d.responsive || {
          breakpoints: { mobile: "768px", tablet: "1024px", desktop: "1280px" }
        }
      });
      setMeta({
        name: (data as any).name,
        description: (data as any).description || "",
        category: (data as any).category,
        preview_image_url: (data as any).preview_image_url || ""
      });
    } catch (e) {
      console.error(e);
      toast({ title: "Fehler", description: "Template konnte nicht geladen werden.", variant: "destructive" });
      navigate("/superadmin/landing-pages");
    } finally {
      setLoading(false);
    }
  };

  const saveTemplate = async () => {
    if (!template || !tplData) return;
    setSaving(true);
    try {
      const { error } = await supabase
        .from("landing_page_templates")
        .update({
          name: meta.name,
          description: meta.description,
          category: meta.category,
          preview_image_url: meta.preview_image_url,
          template_data: JSON.parse(JSON.stringify(tplData)),
          updated_at: new Date().toISOString()
        })
        .eq("id", template.id);
      if (error) throw error;
      toast({ title: "Gespeichert", description: "Template wurde gespeichert." });
    } catch (e) {
      console.error(e);
      toast({ title: "Fehler", description: "Template konnte nicht gespeichert werden.", variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Template Builder wird geladen...</p>
        </div>
      </div>
    );
  }

  if (!template || !tplData) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <p className="text-muted-foreground">Template nicht gefunden</p>
          <Button onClick={() => navigate("/superadmin/landing-pages")} className="mt-4">Zurück</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <header className="border-b bg-card/50 backdrop-blur-sm sticky top-0 z-50">
        <div className="flex items-center justify-between px-6 py-3">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={() => navigate("/superadmin/landing-pages")} className="gap-2">
              <ArrowLeft className="w-4 h-4" />
              Zurück
            </Button>
            <div className="flex items-center gap-2">
              <Badge variant="secondary">Template Builder</Badge>
              <h1 className="text-lg font-semibold">{meta.name}</h1>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button onClick={saveTemplate} disabled={saving} size="sm">
              {saving ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Speichert...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Speichern
                </>
              )}
            </Button>
          </div>
        </div>
      </header>

      <div className="flex-1 flex flex-col">
        <Tabs defaultValue="builder" className="flex-1 flex flex-col">
          <TabsList className="justify-start w-auto mx-6 mt-4">
            <TabsTrigger value="builder">🎨 Visual Builder</TabsTrigger>
            <TabsTrigger value="settings">⚙️ Einstellungen</TabsTrigger>
          </TabsList>

          <TabsContent value="builder" className="flex-1 m-0 p-0">
            <div className="h-[calc(100vh-120px)]">
              <VisualPageBuilder initialData={tplData} onSave={setTplData} onPreview={() => {}} />
            </div>
          </TabsContent>

          <TabsContent value="settings" className="flex-1 p-6">
            <div className="max-w-2xl space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Name</Label>
                  <Input value={meta.name} onChange={(e) => setMeta({ ...meta, name: e.target.value })} />
                </div>
                <div className="space-y-2">
                  <Label>Kategorie</Label>
                  <Input value={meta.category} onChange={(e) => setMeta({ ...meta, category: e.target.value })} />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Beschreibung</Label>
                <Textarea value={meta.description} onChange={(e) => setMeta({ ...meta, description: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Vorschaubild URL</Label>
                <Input value={meta.preview_image_url} onChange={(e) => setMeta({ ...meta, preview_image_url: e.target.value })} />
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
